import {
  ButtonProps,
  SystemStyleObject,
  TextareaProps,
  TextProps,
} from '@chakra-ui/react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { MdEdit } from 'react-icons/md';
import { Tooltip } from '../ui/tooltip';
import { MBox, MIcon } from './chakra';
import MButton from './MButton';
import MText from './MText';
import MTextarea from './MTextarea';

const CONTAINER_FOCUS_PROPS: SystemStyleObject = {
  background: 'tGray.support',
  borderColor: 'tGray.lightPurple',
};

export interface MEditableTextareaProps {
  value: string;
  onSubmit: (value: string) => void;
  onCancel?: (value: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  tooltip?: string;
  noOfLines?: number;
  showTooltipMinChars?: number;
  previewButtonProps?: ButtonProps;
  placeholderProps?: TextProps;
  textareaProps?: TextareaProps;
  'data-testid'?: string;
}

/**
 * An editable textarea component that shows a truncated preview with optional tooltip
 * and expands to a full textarea on edit. Includes save/cancel buttons and
 * a pencil icon for discoverability.
 */
export const MEditableTextarea: FC<MEditableTextareaProps> = ({
  value,
  onSubmit,
  onCancel,
  isDisabled = false,
  placeholder,
  tooltip,
  noOfLines = 3,
  previewButtonProps,
  placeholderProps,
  textareaProps,
  showTooltipMinChars = 75,
  'data-testid': dataTestId,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isCancellingRef = useRef(false);

  // Sync external value changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end of text
      const { length } = textareaRef.current.value;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (isDisabled) {
      return;
    }
    setEditValue(value);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    setIsEditing(false);
    if (trimmedValue !== value.trim()) {
      onSubmit(trimmedValue);
    }
  };

  const handleCancel = () => {
    isCancellingRef.current = true;
    setIsEditing(false);
    setEditValue(value);
    onCancel?.(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Check if focus moved outside the container
    const nextFocused = e.relatedTarget as Node | null;
    if (
      containerRef.current &&
      (!nextFocused || !containerRef.current.contains(nextFocused))
    ) {
      if (isCancellingRef.current) {
        isCancellingRef.current = false;
        return;
      }
      handleSave();
    }
  };

  // Preview mode
  if (!isEditing) {
    const previewContent = (
      <MButton
        data-testid={dataTestId}
        type="button"
        variant="bare"
        display="flex"
        textAlign="left"
        className="editable-container"
        alignItems="flex-start"
        justifyContent="space-between"
        w="100%"
        minH="3em"
        px="0"
        borderColor="transparent"
        borderRadius="3px"
        cursor={isDisabled ? 'default' : 'pointer'}
        fontWeight="normal"
        onClick={handleStartEdit}
        _disabled={{
          cursor: 'default',
          opacity: 1,
        }}
        _hover={isDisabled ? {} : CONTAINER_FOCUS_PROPS}
        _focusWithin={isDisabled ? {} : CONTAINER_FOCUS_PROPS}
        {...previewButtonProps}
      >
        <MText
          fontSize="sm"
          color="tPurple.base"
          lineClamp={noOfLines}
          flex="1"
          minH="1rem"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          {...placeholderProps}
        >
          {value || placeholder}
        </MText>
        {!isDisabled && (
          <MIcon
            as={MdEdit}
            mr={1}
            boxSize="4"
            flexShrink={0}
            alignSelf="center"
            opacity={0}
            transition="opacity 0.2s"
            _active={{ opacity: '1 !important' }}
            _focus={{
              opacity: '1 !important',
              background: 'tGray.lightPurple',
            }}
            css={{
              '.editable-container:hover &': {
                opacity: 1,
              },
              '.editable-container:focus &': {
                opacity: 1,
              },
            }}
          />
        )}
      </MButton>
    );

    if (
      tooltip ||
      (value && (value.length > showTooltipMinChars || value.includes('\n')))
    ) {
      return (
        <Tooltip
          content={
            <span style={{ whiteSpace: 'pre-wrap' }}>{tooltip || value}</span>
          }
          aria-label={tooltip || value}
          showArrow
          positioning={{
            placement: 'top',
          }}
        >
          {previewContent}
        </Tooltip>
      );
    }

    return previewContent;
  }

  // Edit mode
  return (
    <MBox
      ref={containerRef}
      w="100%"
      pb={1}
      onBlur={handleBlur}
      tabIndex={-1}
      data-testid={dataTestId}
    >
      <MTextarea
        ref={textareaRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        fontSize="sm"
        minH="80px"
        resize="vertical"
        {...textareaProps}
      />
    </MBox>
  );
};
